import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import { AuthLayout } from '../../components/auth/auth-layout.tsx';
import { PasswordInput } from '../../components/auth/password-input.tsx';
import { PasswordStrength } from '../../components/auth/password-strength.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Button, buttonVariants } from '../../components/ui/button.tsx';
import { Field, Input } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, ApiError, errorMessage } from '../../lib/api.ts';
import { takeTokenFromHash } from '../../lib/navigation.ts';
import { usePasswordCheck } from '../../lib/use-password-check.ts';

interface Invitation {
  tenantName: string;
  email: string;
  expiresAt: string;
  userExists: boolean;
}

export function AcceptInvitationPage() {
  const [token] = useState(takeTokenFromHash);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [usedOnSubmit, setUsedOnSubmit] = useState(false);

  const lookup = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api<Invitation>('/invitations/lookup', { method: 'POST', body: { token } }),
    enabled: token !== null,
    retry: false,
  });
  const serverMessage = usePasswordCheck(
    '/invitations/check-password',
    { token, fullName },
    password,
  );

  const invalid = (
    <AuthLayout title="Invitación no válida">
      <Alert tone="warning">
        El enlace no es válido o la invitación venció. Pide a tu administrador que te la reenvíe.
      </Alert>
    </AuthLayout>
  );
  if (!token) return invalid;
  if (usedOnSubmit) return <UsedInvitation />;
  if (lookup.isPending) return <Loading label="Revisando tu invitación…" />;
  if (lookup.error) {
    if (lookup.error instanceof ApiError && lookup.error.code === 'invitation_used') {
      return <UsedInvitation />;
    }
    if (lookup.error instanceof ApiError && lookup.error.status === 404) return invalid;
    return (
      <AuthLayout title="Invitación">
        <Alert tone="danger">{errorMessage(lookup.error)}</Alert>
      </AuthLayout>
    );
  }
  const invitation = lookup.data;
  const loginLink = `/iniciar-sesion?correo=${encodeURIComponent(invitation.email)}`;

  if (done) {
    return (
      <AuthLayout title="¡Listo!" subtitle={`Ya eres parte de ${invitation.tenantName}.`}>
        <p className="text-sm">
          {invitation.userExists
            ? 'Inicia sesión con tu contraseña de siempre.'
            : 'Inicia sesión con tu correo y la contraseña que acabas de crear. Después te pediremos activar la verificación en dos pasos.'}
        </p>
        <Link to={loginLink} className={buttonVariants({ className: 'w-full' })}>
          Iniciar sesión
        </Link>
      </AuthLayout>
    );
  }

  const accept = async () => {
    setError(null);
    setFieldError(null);
    if (!invitation.userExists && password !== confirmPassword) {
      setFieldError('Las contraseñas no coinciden.');
      return;
    }
    setBusy(true);
    try {
      await api('/invitations/accept', {
        method: 'POST',
        // For an existing account the name and password are ignored by the API.
        body: invitation.userExists
          ? { token, fullName: invitation.email, password: '' }
          : { token, fullName, password },
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'weak_password') setFieldError(err.message);
      else if (err instanceof ApiError && err.code === 'invitation_used') setUsedOnSubmit(true);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title={`Únete a ${invitation.tenantName}`}
      subtitle={`Invitación para ${invitation.email}`}
    >
      {error && <Alert tone="danger">{error}</Alert>}
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void accept();
        }}
      >
        {invitation.userExists ? (
          <p className="text-sm">
            Ya tienes una cuenta con este correo. Acepta para unirte con tu contraseña actual.
          </p>
        ) : (
          <>
            <Field label="Nombre completo">
              {(a11y) => (
                <Input
                  {...a11y}
                  autoComplete="name"
                  required
                  value={fullName}
                  onChange={(event) => {
                    setFullName(event.target.value);
                  }}
                />
              )}
            </Field>
            <Field label="Contraseña" error={fieldError ?? undefined}>
              {(a11y) => (
                <PasswordInput
                  {...a11y}
                  aria-describedby={[a11y['aria-describedby'], 'password-help']
                    .filter(Boolean)
                    .join(' ')}
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                  }}
                />
              )}
            </Field>
            <PasswordStrength
              id="password-help"
              password={password}
              userInputs={[invitation.email, fullName, invitation.tenantName]}
              serverMessage={serverMessage}
            />
            <Field label="Repite la contraseña">
              {(a11y) => (
                <PasswordInput
                  {...a11y}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => {
                    setConfirmPassword(event.target.value);
                  }}
                />
              )}
            </Field>
          </>
        )}
        <Button
          variant="brand"
          type="submit"
          className="w-full"
          loading={busy}
          disabled={!invitation.userExists && (!fullName.trim() || !password)}
        >
          {invitation.userExists ? 'Aceptar invitación' : 'Crear mi cuenta'}
        </Button>
      </form>
    </AuthLayout>
  );
}

/** The link was already used to create the account: point to the login. */
function UsedInvitation() {
  return (
    <AuthLayout title="Invitación ya usada">
      <Alert tone="info">Esta invitación ya se usó. Si ya creaste tu cuenta, inicia sesión.</Alert>
      <Link
        to="/iniciar-sesion"
        className={buttonVariants({ variant: 'brand', className: 'w-full' })}
      >
        Ir a iniciar sesión
      </Link>
    </AuthLayout>
  );
}
